import { BrowserStorage, FileType } from '@const/consts';
import i18n from '@app/i18n';
import EnvelopesService from '@services/envelopes';
import TokensService from '@services/tokensService';
import { getBaseUrl } from '@utils/utils';

import * as pdfjsLib from 'pdfjs-dist';

const PDF_WORKER_SRC = '/assets/js/pdf.worker.min.js';
const PDF_DATA_CACHE_MAX_ENTRIES = 25;

// Required for PDFs that reference predefined character maps (e.g. Japanese).
export const PDF_DOCUMENT_OPTIONS = {
  cMapUrl: '/assets/pdfjs/cmaps/',
  cMapPacked: true
};

type EnvelopePdfDataCacheEntry = {
  lastUsedAt: number;
  promise: Promise<ArrayBuffer>;
  refCount: number;
}

type EnvelopePdfFileType = `${FileType}.PDF`;

export type RetainedEnvelopePdfData = {
  promise: Promise<ArrayBuffer>;
  release: () => void;
  url: string;
}

const envelopePdfDataCache = new Map<string, EnvelopePdfDataCacheEntry>();

const getEnvelopePdfCacheKey = (
  envelopeOguid: string,
  fileIndex: number | string,
  fileType: string
): string => `${envelopeOguid}:${fileIndex}:${fileType}`;

const cleanupEnvelopePdfDataCache = (): void => {
  if (envelopePdfDataCache.size <= PDF_DATA_CACHE_MAX_ENTRIES) return;

  const removableEntries = Array.from(envelopePdfDataCache.entries())
    .filter(([, entry]) => entry.refCount === 0)
    .sort(([, firstEntry], [, secondEntry]) => firstEntry.lastUsedAt - secondEntry.lastUsedAt);

  removableEntries
    .slice(0, Math.max(0, envelopePdfDataCache.size - PDF_DATA_CACHE_MAX_ENTRIES))
    .forEach(([key]) => envelopePdfDataCache.delete(key));
};

export const setupPdfWorker = (): void => {
  if (pdfjsLib.GlobalWorkerOptions.workerSrc !== PDF_WORKER_SRC) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
  }
};

export const getEnvelopePdfUrl = (
  envelopeOguid: string,
  fileIndex: number | string,
  fileType: string
): string =>
  `${getBaseUrl().replace(/\/+$/, '')}/envelopes/${envelopeOguid}/files/${fileIndex}/type/${fileType}`;

export const getPdfHeaders = (): Record<string, string> => {
  const user = TokensService.getUserFromCookies();
  const accessToken = TokensService.getTokenFromCookies(BrowserStorage.TOKEN_ACCESS, user);

  return {
    'Accept-Language': i18n.language,
    ...(accessToken ? { 'Access-Token': accessToken } : {}),
    'X-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

export const retainEnvelopePdfData = (
  envelopeOguid: string,
  fileIndex: number | string,
  fileType: string
): RetainedEnvelopePdfData => {
  const key = getEnvelopePdfCacheKey(envelopeOguid, fileIndex, fileType);
  const url = getEnvelopePdfUrl(envelopeOguid, fileIndex, fileType);
  const currentEntry = envelopePdfDataCache.get(key);
  const entry = currentEntry ?? {
    lastUsedAt: Date.now(),
    promise: EnvelopesService
      .getEnvelopeFile(envelopeOguid, Number(fileIndex), fileType as EnvelopePdfFileType)
      .then(({ data }) => data)
      .catch((error) => {
        envelopePdfDataCache.delete(key);
        throw error;
      }),
    refCount: 0
  };

  if (!currentEntry) {
    envelopePdfDataCache.set(key, entry);
    cleanupEnvelopePdfDataCache();
  }

  entry.refCount += 1;
  entry.lastUsedAt = Date.now();

  let isReleased = false;

  return {
    promise: entry.promise,
    release: () => {
      if (isReleased) return;

      isReleased = true;
      entry.refCount = Math.max(0, entry.refCount - 1);
      entry.lastUsedAt = Date.now();
      cleanupEnvelopePdfDataCache();
    },
    url
  };
};
